import { useState, useContext } from 'react';
import { AppContext } from '../App';
import { useI18n } from '../i18n/index.jsx';
import api from '../api';

/**
 * Own Company Onboarding — Tier 2
 * 
 * Step 1: Enter NIP → validate (blocks our companies + fakes)
 * Step 2: Confirm/edit company data (auto-filled from GUS)
 * Step 3: Personal data (name, email, phone)
 * Step 4: Done — full access to accounting for their company
 */

const STEPS = ['nip', 'company', 'personal', 'done'];

export default function OwnCompanyOnboarding() {
  const { user, setUser, setPage } = useContext(AppContext);
  const { t } = useI18n();

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // NIP step
  const [nip, setNip] = useState('');
  const [nipValidated, setNipValidated] = useState(false);
  const [companyData, setCompanyData] = useState(null);

  // Company form
  const [companyForm, setCompanyForm] = useState({
    company_name: '',
    nip: '',
    krs: '',
    regon: '',
    address: '',
    legal_form: '',
    is_vat_payer: true,
  });

  // Personal form
  const [personalForm, setPersonalForm] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
    email: user?.email || '',
  });

  const currentStep = STEPS[step];

  // ── Step 1: Check NIP ──
  async function handleCheckNip() {
    const cleaned = nip.trim().replace(/[-\s]/g, '');
    if (!cleaned || cleaned.length !== 10) {
      setError(t('own_company.nip_invalid'));
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await api.post('/onboarding/own-company/check-nip', { nip: cleaned });
      const data = res.data;

      setNipValidated(true);

      const isVat = data.vat_registered === true;

      if (data.company_data) {
        setCompanyData(data.company_data);
        setCompanyForm({
          company_name: data.company_data.name || '',
          nip: data.company_data.nip || cleaned,
          krs: data.company_data.krs || '',
          regon: data.company_data.regon || '',
          address: data.company_data.address || '',
          legal_form: '',
          is_vat_payer: isVat,
          vat_status: data.vat_status || 'unknown',
        });
      } else {
        setCompanyForm(prev => ({ ...prev, nip: cleaned, is_vat_payer: isVat, vat_status: data.vat_status || 'unknown' }));
      }

      if (data.warning) {
        setError('⚠️ ' + data.warning);
      }

      setStep(1);
    } catch (e) {
      const detail = e.response?.data?.detail || e.userMessage || t('own_company.nip_error');
      setError(detail);
    } finally {
      setLoading(false);
    }
  }

  // ── Step 2: Confirm company ──
  function updateCompany(field, value) {
    setCompanyForm(prev => ({ ...prev, [field]: value }));
  }

  function handleCompanyConfirm() {
    if (!companyForm.company_name.trim()) {
      setError(t('own_company.name_required'));
      return;
    }
    setError(null);
    setStep(2);
  }

  // ── Step 3: Personal data + register ──
  function updatePersonal(field, value) {
    setPersonalForm(prev => ({ ...prev, [field]: value }));
  }

  async function handleRegister() {
    if (!personalForm.first_name.trim() || !personalForm.last_name.trim()) {
      setError(t('own_company.name_required_personal'));
      return;
    }
    if (!personalForm.email.trim()) {
      setError(t('own_company.email_required'));
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Save personal data first
      await api.put('/me', {
        first_name: personalForm.first_name.trim(),
        last_name: personalForm.last_name.trim(),
        phone: personalForm.phone.trim(),
        email: personalForm.email.trim(),
      });

      // Register company
      const res = await api.post('/onboarding/own-company/register', {
        nip: companyForm.nip,
        company_name: companyForm.company_name.trim(),
        krs: companyForm.krs || null,
        regon: companyForm.regon || null,
        address: companyForm.address || null,
        legal_form: companyForm.legal_form || null,
        is_vat_payer: companyForm.is_vat_payer,
      });

      // Refresh user data
      try {
        const meRes = await api.get('/me');
        if (setUser) setUser(meRes.data);
      } catch {}

      setStep(3);
    } catch (e) {
      const detail = e.response?.data?.detail || e.userMessage || 'Ошибка регистрации';
      setError(detail);
    } finally {
      setLoading(false);
    }
  }

  function renderField(value, onChange, label, type = 'text', required = false) {
    return (
      <div className="field">
        <label>{label} {required && <span style={{ color: 'var(--accent)' }}>*</span>}</label>
        <input
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={label}
        />
      </div>
    );
  }

  const progressPercent = Math.min(step, 3) / 3 * 100;

  return (
    <div className="onboarding-flow">
      {/* Progress */}
      {step > 0 && step < 3 && (
        <div className="co-progress">
          <div className="co-progress-bar">
            <div className="co-progress-fill" style={{ width: `${progressPercent}%` }} />
          </div>
          <div className="co-progress-text">
            {t('client_onboarding.step')} {step}/3
          </div>
        </div>
      )}

      {error && (
        <div className="banner banner-warning" style={{ margin: '12px 0' }}>
          ⚠️ {error}
        </div>
      )}

      {/* ══════════ Step 1: NIP ══════════ */}
      {currentStep === 'nip' && (
        <div className="onboarding-step">
          <div className="model-icon">🏗️</div>
          <h1>{t('own_company.nip_title')}</h1>
          <p className="model-desc">{t('own_company.nip_subtitle')}</p>

          <div className="field" style={{ width: '100%', marginTop: 20 }}>
            <label>NIP</label>
            <input
              type="text"
              value={nip}
              onChange={e => setNip(e.target.value)}
              placeholder="0000000000"
              maxLength={13}
              style={{ fontSize: 20, textAlign: 'center', fontFamily: 'monospace', letterSpacing: 2 }}
            />
          </div>

          <button
            className="btn btn-primary btn-large"
            style={{ width: '100%', marginTop: 16 }}
            onClick={handleCheckNip}
            disabled={loading}
          >
            {loading ? '⏳ Проверяем...' : t('own_company.check_nip')}
          </button>

          <button className="btn btn-link back-btn" onClick={() => setPage('tier_select')}>
            ← {t('common.back')}
          </button>
        </div>
      )}

      {/* ══════════ Step 2: Company data ══════════ */}
      {currentStep === 'company' && (
        <div className="onboarding-step" style={{ textAlign: 'left', alignItems: 'stretch' }}>
          <h1 style={{ textAlign: 'center' }}>🏢 {t('own_company.company_data')}</h1>

          {companyData && (
            <div style={{
              padding: 12, borderRadius: 10,
              background: 'rgba(16,185,129,0.1)',
              border: '1px solid rgba(16,185,129,0.3)',
              fontSize: 13, marginBottom: 16, textAlign: 'center'
            }}>
              ✅ {t('own_company.data_from_registry')}
            </div>
          )}

          {/* VAT Status Badge */}
          <div style={{
            padding: 10, borderRadius: 10, marginBottom: 16, textAlign: 'center',
            background: companyForm.is_vat_payer ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
            border: `1px solid ${companyForm.is_vat_payer ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
            fontSize: 13,
          }}>
            {companyForm.is_vat_payer
              ? '✅ Płatnik VAT (Czynny) — rejestr VAT/JPK będzie aktywny'
              : `⚠️ Nie jest płatnikiem VAT (${companyForm.vat_status || 'Niezarejestrowany'}) — bez rejestrów VAT`
            }
          </div>

          <div className="form">
            {renderField(companyForm.company_name, v => updateCompany('company_name', v), t('own_company.company_name'), 'text', true)}
            {renderField(companyForm.nip, () => {}, 'NIP', 'text', true)}
            {renderField(companyForm.krs, v => updateCompany('krs', v), 'KRS')}
            {renderField(companyForm.regon, v => updateCompany('regon', v), 'REGON')}
            {renderField(companyForm.address, v => updateCompany('address', v), t('own_company.address'))}
            {renderField(companyForm.legal_form, v => updateCompany('legal_form', v), t('own_company.legal_form'))}

            <label className="checkbox-item" onClick={() => updateCompany('is_vat_payer', !companyForm.is_vat_payer)}>
              <input type="checkbox" checked={companyForm.is_vat_payer} readOnly />
              <span>{t('own_company.vat_payer')}</span>
            </label>
          </div>

          <button
            className="btn btn-primary btn-large"
            style={{ width: '100%', marginTop: 16 }}
            onClick={handleCompanyConfirm}
          >
            {t('common.next')} →
          </button>

          <button className="btn btn-link back-btn" onClick={() => setStep(0)}>
            ← {t('common.back')}
          </button>
        </div>
      )}

      {/* ══════════ Step 3: Personal data ══════════ */}
      {currentStep === 'personal' && (
        <div className="onboarding-step" style={{ textAlign: 'left', alignItems: 'stretch' }}>
          <h1 style={{ textAlign: 'center' }}>👤 {t('own_company.personal_title')}</h1>

          <div className="form">
            {renderField(personalForm.first_name, v => updatePersonal('first_name', v), t('client_onboarding.first_name'), 'text', true)}
            {renderField(personalForm.last_name, v => updatePersonal('last_name', v), t('client_onboarding.last_name'), 'text', true)}
            {renderField(personalForm.phone, v => updatePersonal('phone', v), t('client_onboarding.phone'), 'tel')}
            {renderField(personalForm.email, v => updatePersonal('email', v), t('client_onboarding.email'), 'email', true)}
          </div>

          <button
            className="btn btn-primary btn-large"
            style={{ width: '100%', marginTop: 16 }}
            onClick={handleRegister}
            disabled={loading}
          >
            {loading ? '⏳...' : t('own_company.register_btn')}
          </button>

          <button className="btn btn-link back-btn" onClick={() => setStep(1)}>
            ← {t('common.back')}
          </button>
        </div>
      )}

      {/* ══════════ Step 4: Done ══════════ */}
      {currentStep === 'done' && (
        <div className="onboarding-step success">
          <div className="success-icon">🎉</div>
          <h1>{t('own_company.done_title')}</h1>
          <p className="model-desc">{t('own_company.done_subtitle')}</p>

          <div style={{
            marginTop: 20, padding: 16,
            background: 'rgba(16,185,129,0.1)',
            borderRadius: 12, border: '1px solid rgba(16,185,129,0.3)',
            textAlign: 'left', fontSize: 14
          }}>
            <div><strong>{t('own_company.company_name')}:</strong> {companyForm.company_name}</div>
            <div><strong>NIP:</strong> {companyForm.nip}</div>
            {companyForm.krs && <div><strong>KRS:</strong> {companyForm.krs}</div>}
          </div>

          <button
            className="btn btn-primary btn-large"
            style={{ marginTop: 20 }}
            onClick={() => setPage('dashboard')}
          >
            {t('client_onboarding.go_dashboard')} →
          </button>
        </div>
      )}
    </div>
  );
}
